import * as Clipboard from "expo-clipboard";
import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import { Platform, ScrollView } from "react-native";
// @ts-expect-error - b2wordcloud doesn't have types
import B2wordcloud from "b2wordcloud";

import { Box, Center, Text } from "@/components/ui";
import { useLocalization } from "@/hooks";
import { WordCloudTerm } from "@/types/resources";

interface WordCloudProps {
  words: WordCloudTerm[];
}

export interface WordCloudRef {
  copy: () => Promise<void>;
}

const NEUTRAL_COLOR = "#9ca3af";

const getWordColor = (
  term: WordCloudTerm,
  maxWeight: number,
  isTop5: boolean
): string => {
  const weightRatio = term.weight / maxWeight;
  // Linear ratio for maximum gradient spread between words
  const gradientRatio = weightRatio;

  if (term.sentiment === "positive") {
    if (!isTop5) {
      return NEUTRAL_COLOR;
    }
    // Muted green gradient: from soft sage (#d4e5d7) to muted teal (#5a9a7a)
    const lightR = 212;
    const lightG = 229;
    const lightB = 215;
    const darkR = 90;
    const darkG = 154;
    const darkB = 122;
    const r = Math.round(darkR * gradientRatio + lightR * (1 - gradientRatio));
    const g = Math.round(darkG * gradientRatio + lightG * (1 - gradientRatio));
    const b = Math.round(darkB * gradientRatio + lightB * (1 - gradientRatio));
    return `rgb(${r}, ${g}, ${b})`;
  } else if (term.sentiment === "negative") {
    if (!isTop5) {
      return NEUTRAL_COLOR;
    }
    // Muted red gradient: from soft rose (#ebcfcf) to dusty red (#c06565)
    const lightR = 235;
    const lightG = 207;
    const lightB = 207;
    const darkR = 192;
    const darkG = 101;
    const darkB = 101;
    const r = Math.round(darkR * gradientRatio + lightR * (1 - gradientRatio));
    const g = Math.round(darkG * gradientRatio + lightG * (1 - gradientRatio));
    const b = Math.round(darkB * gradientRatio + lightB * (1 - gradientRatio));
    return `rgb(${r}, ${g}, ${b})`;
  } else {
    return NEUTRAL_COLOR;
  }
};

const WordCloud = forwardRef<WordCloudRef, WordCloudProps>(({ words }, ref) => {
  const { i18n } = useLocalization();
  const containerRef = useRef<HTMLDivElement | null>(null);
  const wordCloudInstanceRef = useRef<B2wordcloud | null>(null);
  const wordMapRef = useRef<Map<string, WordCloudTerm>>(new Map());
  const [isVisible, setIsVisible] = useState(false);
  const hasRenderedRef = useRef(false);

  const maxWeight = useMemo(() => {
    return words.length > 0 ? Math.max(...words.map((w) => w.weight)) : 1;
  }, [words]);

  const top5Words = useMemo(() => {
    if (words.length === 0) return new Set<string>();
    // Sort by weight descending and get top 5
    const sorted = [...words].sort((a, b) => b.weight - a.weight);
    return new Set(sorted.slice(0, 5).map((w) => w.term));
  }, [words]);

  const wordCloudData: [string, number][] = useMemo(() => {
    if (words.length === 0) return [];
    wordMapRef.current = new Map(words.map((w) => [w.term, w]));
    return words.map((term) => [term.term, term.weight]);
  }, [words]);

  // Use IntersectionObserver to detect when component becomes visible
  useEffect(() => {
    if (Platform.OS !== "web" || !containerRef.current) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (entry.isIntersecting && !hasRenderedRef.current) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    observer.observe(containerRef.current);

    return () => observer.disconnect();
  }, []);

  // Render word cloud when visible and data is ready
  useEffect(() => {
    if (
      Platform.OS !== "web" ||
      !containerRef.current ||
      wordCloudData.length === 0 ||
      !isVisible
    ) {
      return;
    }

    const container = containerRef.current;

    if (wordCloudInstanceRef.current) {
      container.innerHTML = "";
    }

    hasRenderedRef.current = true;

    const wordList = wordCloudData
      .slice(0, 30)
      .map(([text, weight]) => [text, weight]);
    const shuffledWordList = [...wordList].sort(() => Math.random() - 0.5);

    wordCloudInstanceRef.current = new B2wordcloud(container, {
      list: shuffledWordList,
      gridSize: 8,
      weightFactor: (size: number) => {
        return Math.pow(size, 0.5) * 6;
      },
      fontFamily: "Arial, sans-serif",
      color: (word: string) => {
        const term = wordMapRef.current.get(word);
        if (!term) return NEUTRAL_COLOR;
        const isTop5 = top5Words.has(word);
        return getWordColor(term, maxWeight, isTop5);
      },
      rotateRatio: 0,
      preferHorizontal: true,
      shuffle: false,
      shape: "rectangle",
      ellipticity: 0.4,
      drawOutOfBound: false,
      minFontSize: 12,
      maxFontSize: 50,
      backgroundColor: "transparent",
      tooltip: {
        show: true,
        formatter: (item: [string, number]) => {
          const term = wordMapRef.current.get(item[0]);
          if (term && term.reason) {
            return `<div style="padding: 8px; font-family: Arial, sans-serif;"><strong>${term.term}</strong><br/>${term.reason}</div>`;
          }
          return "";
        },
      },
    });

    return () => {
      if (container) {
        container.innerHTML = "";
      }
      wordCloudInstanceRef.current = null;
    };
  }, [wordCloudData, maxWeight, top5Words, isVisible]);

  const handleCopy = async () => {
    if (!containerRef.current) return;
    try {
      const canvas = containerRef.current.querySelector("canvas");
      if (!canvas) return;

      canvas.toBlob(async (blob) => {
        if (!blob) return;
        const reader = new FileReader();
        reader.onloadend = async () => {
          const base64String = (reader.result as string).split(",")[1];
          await Clipboard.setImageAsync(base64String);
        };
        reader.readAsDataURL(blob);
      }, "image/png");
    } catch (error) {
      console.error("Failed to copy word cloud:", error);
    }
  };

  useImperativeHandle(ref, () => ({
    copy: handleCopy,
  }));

  if (words.length === 0) {
    return (
      <Center className="h-[450px] w-full">
        <Text className="text-typography-500">
          {i18n.t("dashboardNavigator.noData")}
        </Text>
      </Center>
    );
  }

  return (
    <Box>
      <ScrollView
        horizontal
        showsHorizontalScrollIndicator={true}
        className="w-full h-[450px]"
        contentContainerClassName="items-center justify-center"
      >
        {Platform.OS === "web" && (
          <div
            ref={containerRef}
            style={{
              width: "600px",
              height: "430px",
            }}
          />
        )}
      </ScrollView>
    </Box>
  );
});

WordCloud.displayName = "WordCloud";

export default WordCloud;
